// src/pressable.connect.ts
import { getNativeEvent, isVirtualClick, isVirtualPointerEvent } from "@zag-js/dom-event";
import { contains, dataAttr } from "@zag-js/dom-query";

// src/pressable.utils.ts
import { isHTMLElement } from "@zag-js/dom-query";
function isValidKeyboardEvent(event) {
  const { key, target } = event;
  const element = target;
  const { tagName, isContentEditable } = element;
  const role = element.getAttribute("role");
  if (tagName !== "INPUT" && tagName !== "TEXTAREA" && isContentEditable)
    return false;
  return (key === "Enter" || key === " ") && // A link with a valid href should be handled natively,
  // unless it also has role='button' and was triggered using Space.
  (!isHTMLAnchorLink(element) || role === "button" && key !== "Enter") && // An element with role='link' should only trigger with Enter key
  !(role === "link" && key !== "Enter");
}
function isHTMLAnchorLink(target) {
  return target.tagName === "A" && target.hasAttribute("href");
}
function shouldPreventDefaultKeyboard(target) {
  return !((target.tagName === "INPUT" || target.tagName === "BUTTON") && target.type === "submit");
}
function shouldPreventDefault(target) {
  return !isHTMLElement(target) || !target.draggable;
}
function isOverTarget(point, target) {
  if (!target)
    return;
  let rect = target.getBoundingClientRect();
  let pointRect = getPointClientRect(point);
  return areRectanglesOverlapping(rect, pointRect);
}
function getPointClientRect(point) {
  let offsetX = point.width ? point.width / 2 : point.radiusX || 0;
  let offsetY = point.height ? point.height / 2 : point.radiusY || 0;
  return {
    top: point.clientY - offsetY,
    right: point.clientX + offsetX,
    bottom: point.clientY + offsetY,
    left: point.clientX - offsetX
  };
}
function areRectanglesOverlapping(a, b) {
  if (a.left > b.right || b.left > a.right) {
    return false;
  }
  if (a.top > b.bottom || b.top > a.bottom) {
    return false;
  }
  return true;
}

// src/pressable.connect.ts
function connect(state, send, normalize) {
  const isPressed = state.hasTag("pressed");
  const isDisabled = state.context.disabled;
  return {
    isPressed,
    pressableProps: normalize.element({
      "data-disabled": dataAttr(isDisabled),
      "data-pressed": dataAttr(isPressed),
      onKeyDown(event) {
        const evt = getNativeEvent(event);
        if (!isValidKeyboardEvent(evt))
          return;
        if (!contains(event.currentTarget, event.target))
          return;
        if (!event.repeat) {
          const currentTarget = event.currentTarget;
          send({ type: "KEY_DOWN", currentTarget, pointerType: "keyboard" });
        }
        if (shouldPreventDefaultKeyboard(event.target)) {
          event.preventDefault();
        }
      },
      onClick(event) {
        const evt = getNativeEvent(event);
        if (!contains(event.currentTarget, event.target) || event.button !== 0)
          return;
        const ctx = state.context;
        if (ctx.disabled) {
          event.preventDefault();
        }
        const isVirtual = ctx.pointerType === "virtual" || isVirtualClick(evt);
        if (!ctx.ignoreClickAfterPress && isVirtual) {
          const currentTarget = event.currentTarget;
          send({ type: "CLICK", currentTarget, pointerType: "virtual" });
        }
      },
      onPointerDown(event) {
        if (isDisabled)
          return;
        if (event.button !== 0 || !contains(event.currentTarget, event.target)) {
          return;
        }
        if (shouldPreventDefault(event.currentTarget)) {
          event.preventDefault();
        }
        const evt = getNativeEvent(event);
        const pointerType = isVirtualPointerEvent(evt) ? "virtual" : event.pointerType;
        const pointerId = evt.pointerId;
        const currentTarget = event.currentTarget;
        send({ type: "POINTER_DOWN", currentTarget, pointerType, pointerId });
      },
      onMouseDown(event) {
        if (event.button !== 0)
          return;
        if (shouldPreventDefault(event.currentTarget)) {
          event.preventDefault();
        }
      },
      onDragStart(event) {
        const currentTarget = event.currentTarget;
        send({ type: "DRAG_START", currentTarget });
      }
    })
  };
}

// src/pressable.machine.ts
import { createMachine, ref } from "@zag-js/core";
import { addDomEvent } from "@zag-js/dom-event";
import { isHTMLElement as isHTMLElement2 } from "@zag-js/dom-query";
import { disableTextSelection, restoreTextSelection } from "@zag-js/text-selection";
import { compact } from "@zag-js/utils";

// src/pressable.dom.ts
import { createScope } from "@zag-js/dom-query";
var dom = createScope({});

// src/pressable.machine.ts
function machine(userContext) {
  const ctx = compact(userContext);
  return createMachine(
    {
      id: "pressable",
      initial: "idle",
      context: {
        longPressDelay: 500,
        ...ctx,
        wasPressedDown: false,
        ignoreClickAfterPress: false,
        target: null,
        pointerId: null,
        pointerType: null,
        cleanups: ref([])
      },
      exit: ["restoreTextSelection", "removeDocumentListeners"],
      states: {
        idle: {
          entry: ["resetPointerContext", "restoreTextSelection", "removeDocumentListeners"],
          on: {
            POINTER_DOWN: [
              {
                guard: "isVirtualPointer",
                actions: "setPointerType"
              },
              {
                target: "pressed:in",
                actions: [
                  "setPressedDown",
                  "setPointerType",
                  "setPointerId",
                  "setTarget",
                  "focusIfNeeded",
                  "disableTextSelection",
                  "invokeOnPressStart",
                  "trackDocumentPointerEvents"
                ]
              }
            ],
            KEY_DOWN: {
              target: "pressed:in",
              actions: ["setTarget", "setPointerType", "invokeOnPressStart", "trackDocumentKeyup"]
            },
            CLICK: {
              actions: [
                "focusIfNeeded",
                "invokeOnPressStart",
                "enableClickAfterPress",
                "invokeOnPressEnd",
                "invokeOnPress"
              ]
            }
          }
        },
        "pressed:in": {
          tags: "pressed",
          entry: "preventContextMenu",
          after: {
            LONG_PRESS_DELAY: {
              guard: "wasPressedDown",
              actions: ["clearPressedDown", "invokeOnLongPress"]
            }
          },
          on: {
            POINTER_LEAVE: [
              {
                guard: "cancelOnPointerExit",
                target: "idle",
                actions: ["clearPressedDown", "invokeOnPressEnd"]
              },
              {
                target: "pressed:out",
                actions: ["invokeOnPressEnd"]
              }
            ],
            DOC_POINTER_UP: [
              {
                guard: "wasPressedDown",
                target: "idle",
                actions: [
                  "clearPressedDown",
                  "invokeOnPressUp",
                  "enableClickAfterPress",
                  "invokeOnPressEnd",
                  "invokeOnPress"
                ]
              },
              {
                target: "idle",
                actions: ["clearPressedDown", "invokeOnPressUp", "enableClickAfterPress", "invokeOnPressEnd"]
              }
            ],
            DOC_KEY_UP: {
              target: "idle",
              actions: [
                "clearPressedDown",
                "invokeOnPressUp",
                "disableClickAfterPress",
                "invokeOnPressEnd",
                "invokeOnPress",
                "triggerClickIfAnchor"
              ]
            },
            DOC_POINTER_CANCEL: {
              target: "idle",
              actions: "clearPressedDown"
            },
            DRAG_START: {
              target: "idle",
              actions: "clearPressedDown"
            }
          }
        },
        "pressed:out": {
          on: {
            POINTER_ENTER: {
              target: "pressed:in",
              actions: "invokeOnPressStart"
            },
            DOC_POINTER_UP: {
              target: "idle"
            },
            DOC_POINTER_CANCEL: "idle",
            DRAG_START: "idle"
          }
        }
      }
    },
    {
      delays: {
        LONG_PRESS_DELAY: (ctx2) => ctx2.longPressDelay
      },
      guards: {
        isVirtualPointer: (_ctx, evt) => evt.pointerType === "virtual",
        cancelOnPointerExit: (ctx2) => !!ctx2.cancelOnPointerExit,
        wasPressedDown: (ctx2) => ctx2.wasPressedDown
      },
      actions: {
        trackDocumentPointerEvents(ctx2, _evt, { send }) {
          const doc = dom.getDoc(ctx2);
          const onPointerMove = (event) => {
            if (event.pointerId !== ctx2.pointerId)
              return;
            const isOver = isOverTarget(event, ctx2.target);
            send({
              type: isOver ? "POINTER_ENTER" : "POINTER_LEAVE",
              currentTarget: event.currentTarget,
              pointerType: event.pointerType
            });
          };
          const onPointerUp = (event) => {
            if (event.pointerId !== ctx2.pointerId || event.button !== 0)
              return;
            send({
              type: "DOC_POINTER_UP",
              currentTarget: event.currentTarget,
              pointerType: event.pointerType
            });
          };
          const onPointerCancel = (event) => {
            send({
              type: "DOC_POINTER_CANCEL",
              currentTarget: event.currentTarget
            });
          };
          const cleanup = [
            addDomEvent(doc, "pointermove", onPointerMove, false),
            addDomEvent(doc, "pointerup", onPointerUp, false),
            addDomEvent(doc, "pointercancel", onPointerCancel, false)
          ];
          if (ctx2.pointerType !== "touch") {
            cleanup.push(addDomEvent(doc, "contextmenu", onPointerCancel, false));
          }
          ctx2.cleanups.push(...cleanup);
        },
        trackDocumentKeyup(ctx2, evt, { send }) {
          const doc = dom.getDoc(ctx2);
          const onKeyup = (event) => {
            if (!isValidKeyboardEvent(event))
              return;
            if (shouldPreventDefaultKeyboard(event.target)) {
              event.preventDefault();
            }
            send({
              type: "DOC_KEY_UP",
              // forward the previously store keyboard type
              pointerType: evt.pointerType
            });
          };
          const cleanup = addDomEvent(doc, "keyup", onKeyup, false);
          ctx2.cleanups.push(cleanup);
        },
        removeDocumentListeners(ctx2) {
          ctx2.cleanups.forEach((fn) => fn?.());
          ctx2.cleanups = ref([]);
        },
        resetPointerContext(ctx2) {
          ctx2.pointerId = null;
          ctx2.pointerType = null;
        },
        disableTextSelection(ctx2) {
          if (!ctx2.target || ctx2.allowTextSelectionOnPress)
            return;
          disableTextSelection({ target: ctx2.target, doc: dom.getDoc(ctx2) });
        },
        restoreTextSelection(ctx2) {
          if (ctx2.allowTextSelectionOnPress || !ctx2.target)
            return;
          restoreTextSelection({ target: ctx2.target, doc: dom.getDoc(ctx2) });
        },
        setPointerToVirtual(ctx2) {
          ctx2.pointerType = "virtual";
        },
        setPointerType(ctx2, evt) {
          ctx2.pointerType = evt.pointerType;
        },
        setPointerId(ctx2, evt) {
          ctx2.pointerId = evt.pointerId;
        },
        setTarget(ctx2, evt) {
          ctx2.target = ref(evt.currentTarget);
        },
        focusIfNeeded(ctx2, evt) {
          if (ctx2.disabled || ctx2.preventFocusOnPress)
            return;
          evt.currentTarget.focus({ preventScroll: true });
        },
        invokeOnPressStart(ctx2, evt) {
          if (ctx2.disabled)
            return;
          ctx2.onPressStart?.({
            type: "pressstart",
            pointerType: evt.pointerType || ctx2.pointerType,
            target: evt.currentTarget
          });
        },
        invokeOnPressUp(ctx2, evt) {
          if (ctx2.disabled)
            return;
          ctx2.onPressUp?.({
            type: "pressup",
            pointerType: evt.pointerType || ctx2.pointerType,
            target: evt.currentTarget
          });
        },
        invokeOnPressEnd(ctx2, evt) {
          if (ctx2.disabled)
            return;
          ctx2.onPressEnd?.({
            type: "pressend",
            pointerType: evt.pointerType || ctx2.pointerType,
            target: evt.currentTarget
          });
        },
        invokeOnPress(ctx2, evt) {
          if (ctx2.disabled)
            return;
          ctx2.onPress?.({
            type: "press",
            pointerType: evt.pointerType || ctx2.pointerType,
            target: evt.currentTarget
          });
        },
        triggerClickIfAnchor(ctx2, evt) {
          let target = evt.currentTarget;
          if (!isHTMLElement2(ctx2.target))
            return;
          const isAnchor = isHTMLAnchorLink(ctx2.target) || ctx2.target.getAttribute("role") === "link";
          if (ctx2.target.contains(target) && isAnchor) {
            ctx2.target.click();
          }
        },
        invokeOnLongPress(ctx2, evt) {
          if (!ctx2.target)
            return;
          ctx2.onLongPress?.({
            type: "longpress",
            pointerType: evt.pointerType || ctx2.pointerType,
            target: ctx2.target
          });
        },
        disableClickAfterPress(ctx2) {
          ctx2.ignoreClickAfterPress = true;
        },
        enableClickAfterPress(ctx2) {
          ctx2.ignoreClickAfterPress = false;
        },
        setPressedDown(ctx2) {
          ctx2.wasPressedDown = true;
        },
        clearPressedDown(ctx2) {
          ctx2.wasPressedDown = false;
        },
        preventContextMenu(ctx2) {
          if (ctx2.pointerType !== "touch" || !ctx2.onLongPress)
            return;
          const onContextMenu = (event) => event.preventDefault();
          const cleanup = addDomEvent(ctx2.target, "contextmenu", onContextMenu, { once: true });
          const onPointerUp = () => void setTimeout(cleanup, 30);
          addDomEvent(dom.getWin(ctx2), "pointerup", onPointerUp, { once: true });
        }
      }
    }
  );
}
export {
  connect,
  machine
};
//# sourceMappingURL=index.mjs.map